import React from 'react';
import get from 'lodash/get';
import { Box, Theme, Typography } from '@mui/material';
import {
  EventReportAttributes,
  EventReportStatus,
} from '@loyalty/shared-common/event-processing-api-client';
import { EventReportDetailsProperties } from '@loyalty/shared-common/rule-api-client';
import { ProgramAttributes } from '@loyalty/shared-common/gamification-api-client';
import { makeStyles } from '@mui/styles';
import { useTranslation } from 'react-i18next';
import { SxStyles } from '../../../shared/utils/types';
import { isInShell } from '../../../shell/shell';
import { useEventPropertiesTranslations } from '../../../rules/rule-table-constants';

const useStyles = makeStyles<Theme>(({ spacing }) => ({
  list: {
    margin: 0,
    padding: 0,
  },
}));

const styles: SxStyles = {
  listItem: {
    alignItems: 'center',
    display: 'flex',
    minWidth: 0,
    paddingX: 1,
    paddingY: 1,
  },
  property: {
    flex: '0 0 40%',
    fontWeight: 600,
    minWidth: 0,
  },
  value: {
    flex: '1 1 60%',
    marginLeft: '5px',
    minWidth: 0,
    overflowWrap: 'anywhere',
  },
};

interface Props {
  eventReport: EventReportAttributes;
  program?: ProgramAttributes | null;
}

const properties: EventReportDetailsProperties[] = [
  'id',
  'eventSource',
  'eventName',
  'programId',
  'programName',
  'clientEventId',
  'eventTimestamp',
  'eventCreatedAt',
  'createdAt',
  'eventId',
  'status',
  'isRepublished',
];

export function EventReportDetailsList({ eventReport, program }: Props): React.ReactElement {
  const classes = useStyles();
  const { t } = useTranslation();
  const sourceTranslationProperties = useEventPropertiesTranslations();

  return (
    <ul className={classes.list}>
      {properties.map(prop => (
        <Box key={prop} component="li" sx={styles.listItem}>
          <Typography variant="body1" component="div" sx={styles.property}>
            {sourceTranslationProperties[prop]}
          </Typography>
          <Typography variant="body1" component="div" sx={styles.value}>
            <>{showPropertyValue(eventReport, prop, program)}</>
          </Typography>
        </Box>
      ))}
    </ul>
  );
}

function showPropertyValue(
  eventReport: EventReportAttributes,
  prop: EventReportDetailsProperties,
  program: ProgramAttributes | null | undefined,
) {
  switch (prop) {
    case 'programName':
      return program?.name ?? '-';
    case 'isRepublished':
      return computeRepublishedLink(eventReport);
    default:
      return String(get(eventReport, prop, '-') ?? '-');
  }
}

function getAppWindow(): Window {
  return isInShell() ? window.parent : window;
}

function computeRepublishedLink(eventReport: EventReportAttributes) {
  const { t } = useTranslation();
  const appWindow = getAppWindow();

  if (!eventReport.isRepublished) {
    if (eventReport.status === EventReportStatus.orphanReprocessed) {
      const newUrl = new URL(appWindow.location.href);
      newUrl.search = '';
      newUrl.searchParams.set('clientEventId', eventReport.clientEventId ?? '');
      newUrl.searchParams.set('isRepublished', 'true');
      return (
        <a href={newUrl.toString()} target="_blank" rel="noreferrer">
          {t('event.report.details.properties.republished.final.open')}
        </a>
      );
    } else {
      return 'False';
    }
  } else {
    const newUrl = new URL(appWindow.location.href);
    newUrl.search = '';
    newUrl.searchParams.set('clientEventId', eventReport.clientEventId ?? '');
    newUrl.searchParams.set('isRepublished', 'false');
    newUrl.searchParams.set('endDate', String(eventReport.createdAt));
    return (
      <a href={newUrl.toString()} target="_blank" rel="noreferrer">
        {t('event.report.details.properties.republished.original.open')}
      </a>
    );
  }
}
